import React, { useCallback, useRef } from 'react';
import { connect } from 'react-redux';
import { NavLink } from 'react-router-dom';
import Bullets from 'slickpay/common/Bullets.component';
import Banner from 'slickpay/no-reader/Banner.component';

import IMG_BOOKING_INTG from 'assets/images/websites-portfolio/booking-integration.png';
import IMG_HERO_BANNER from 'assets/images/websites-portfolio/hero-banner.png';
import IMG_HERO_BANNER_MOBILE from 'assets/images/websites-portfolio/hero-banner-mobile.png';
import IMG_SOCIAL_INTG from 'assets/images/websites-portfolio/social-integration.png';
import IMG_WEB_PRICING from 'assets/images/websites-portfolio/update-pricing.png';
import Button from 'components/form/Button.component';
import AlertPanel from 'components/generic/AlertPanel.component';
import Badge from 'components/generic/Badge.component';
import ExternalLink from 'components/generic/ExternalLink.component';
import Icon from 'components/generic/Icon.component';
import ResponsiveBox from 'components/generic/ResponsiveBox.component';
import SidebarMenu from 'components/menu/SidebarMenu.component';
import CurrencySign from 'components/price/CurrencySign.component';
import ImageText from 'components/websites/components/ImageText.component';
import PagesShowcase from 'components/websites/components/PagesShowcase.component';
import Pricing from 'components/websites/components/Pricing.component';
import Showcase from 'components/websites/components/Showcase.component';
import VideoCard from 'components/websites/components/VideoCard.component';
import WebsiteTips from 'components/websites/components/WebsiteTips.component';
import LINK from 'constants/link.const';
import S from 'constants/string.const';
import useWindowScroll from 'hooks/useWindowScroll.hook';
import { useCurrentSalonId } from 'providers/SalonBasicInfo.provider';
import { selectCurrentSalonWebsiteCustomer } from 'selectors/currentSalon.selectors';
import elementScrollTo from 'utils/dom/elementScrollTo.util';
import alwaysNoop from 'utils/functions/nullary/alwaysNoop.util';
import linkTo from 'utils/links/linkTo.util';
import deRef from 'utils/objects/deRef.util';
import C from 'utils/values/constant.util';

import TopNav from 'common/TopNav.component';

import CN from './Portfolio.module.scss';

const VIDEO_LINK = 'https://www.youtube.com/embed/lgavPTDV6b0?si=eWIcgc4-NYmtVfkM';
const VIDEO_TITLE = 'Building my website with Slick was so easy';

const LINK_FORM = 'https://form.jotform.com/242702684038053';
const LINK_REQUEST_CHANGES = 'https://form.jotform.com/243243995450057';

const USE_REQUEST_CHANGES_BUTTON = false;

const DATA_TOP_BANNER = C(['A brand new website in 72 hours', 'Syncs beautifully with your online booking', 'Integrated with Google reviews & socials']);

// NOTE: Not in use at the moment. Needs to be discussed

const Portfolio = ({ isWebsiteCustomer }) => {
  useWindowScroll(0, 0);

  const areaPricing = useRef(null);
  const salonId = useCurrentSalonId();

  const handleScrollToPricing = useCallback(() => void elementScrollTo({ element: deRef(areaPricing), block: 'center' }), []);

  return (
    <div data-bem="Portfolio" className={CN.component}>
      <TopNav title="Design your website">
        <SidebarMenu />
      </TopNav>
      {isWebsiteCustomer ? (
        <div className={CN.wrap}>
          <Banner tag="Portfolio__Banner-existing-websites">
            <div className={CN.heroBanner}>
              <div className={CN.bannerExistingWebContainer}>
                <div className={CN.topBannerTitle}>Your Slick website is already live 🥳</div>
                <div className={CN.existingBannerSubTitle}>
                  “If you need to request any changes please submit a website request ticket on live chat. The team will review and action for you within 48 hours. You will receive updates on live
                  chat.”
                </div>

                {USE_REQUEST_CHANGES_BUTTON && (
                  <div className={CN.webBannerLink}>
                    <ExternalLink link={LINK_REQUEST_CHANGES}>
                      <Button onClick={alwaysNoop} variant="white" width="medium">
                        <span className={CN.buttonSpace}>REQUEST CHANGES</span>
                      </Button>
                    </ExternalLink>
                  </div>
                )}
              </div>
            </div>
          </Banner>
          <div className={CN.webGap}>
            <ImageText imgSrc={IMG_WEB_PRICING} imgAlt="update your Pricelist & Business Info" tag="ImageText__existing-websites">
              <div className={CN.badgeContainer}>
                <Badge name="Slick Websites" variant="fine" />
              </div>
              <div className={CN.webImgTitle}>Easily update your Pricelist & Business Info</div>
              <div className={CN.webImgDescription}>
                Now you have a website with us it is super easy to update your service prices and business info right from your Slick account! Head to ‘Staff & Business Settings’ in the main
                navigation or use the quick links below.
              </div>
              <div className={CN.webButtonsContainer} data-bem="Portfolio__ImageText-buttons">
                <NavLink to={linkTo({ pattern: LINK.services, params: { salonId }, fault: '#' })}>
                  <Button onClick={alwaysNoop} variant="white" width="medium">
                    <span className={CN.buttonSpace}>PRICELIST</span>
                  </Button>
                </NavLink>
                <NavLink to={linkTo({ pattern: LINK.salonHours, params: { salonId }, fault: '#' })}>
                  <Button onClick={alwaysNoop} variant="white" width="medium">
                    <span className={CN.buttonSpace}>BUSINESS INFO</span>
                  </Button>
                </NavLink>
              </div>
            </ImageText>
          </div>
        </div>
      ) : (
        <div>
          <div className={CN.wrap}>
            <Banner tag="Portfolio__Banner-header">
              <div className={CN.heroBanner}>
                <div className={CN.bannerHeaderContainer}>
                  <div className={CN.topBannerTitle}>Get a new Website the Slick Way</div>
                  <Bullets data={DATA_TOP_BANNER} variantInvert checkmarkWhite />
                  <div className={CN.bannerLink}>
                    <Button onClick={handleScrollToPricing} variant="white" width="medium">
                      <span className={CN.buttonSpace}>VIEW PRICING</span>
                    </Button>
                  </div>
                </div>
                <div className={CN.heroImgContainer}>
                  <ResponsiveBox.Desktop>
                    <div className={CN.heroImgContainer}>
                      <img src={IMG_HERO_BANNER} alt="Slick hero banner" className={CN.heroImg} />
                    </div>
                  </ResponsiveBox.Desktop>
                  <ResponsiveBox.Mobile>
                    <div className={CN.heroImgContainer}>
                      <img src={IMG_HERO_BANNER_MOBILE} alt="Slick hero banner" className={CN.heroImg} />
                    </div>
                  </ResponsiveBox.Mobile>
                </div>
              </div>
            </Banner>

            <div className={CN.videoGap}>
              <div className={CN.title}>“Building my website with Slick was so easy"</div>
              <div className={CN.overviewTitle}>Stacey, Harper & Quinn</div>
            </div>

            <ImageText
              variant="inFirstColumn" // NOTE: when variant: push img in first column
              imgSrc={IMG_WEB_PRICING}
              imgAlt="update your Pricelist & Business Info"
              tag="ImageText__existing-websites"
            >
              <div className={CN.badgeContainer}>
                <Badge name="Why Slick Websites" variant="fine" />
              </div>
              <div className={CN.webImgTitle}>Slick Integration</div>
              <div className={CN.webImgDescription}>
                Tired of having to update your prices in multiple places? Thanks to Slick Integration, you just have to update once and Slick takes care of the rest!
              </div>
            </ImageText>
          </div>

          <div data-bem="Portfolio__social-integration" className={CN.socialIntegration}>
            <div className={CN.wrap}>
              <div className={CN.socialIntegrationContainer}>
                <div className={CN.column}>
                  <div className={CN.socialContainer}>
                    Reviews & Social Integration
                    <span className={CN.socialSubTitle}>Clients can see how brilliant you are!</span>
                  </div>
                  <img src={IMG_SOCIAL_INTG} alt=" Reviews & Social Integration" className={CN.socialImg} />
                </div>
                <div className={CN.column}>
                  <div className={CN.socialContainer}>
                    Online Booking Integration
                    <span className={CN.socialSubTitle}>Make it easy for clients to book with you!</span>
                  </div>
                  <img src={IMG_BOOKING_INTG} alt=" Online Booking Integration" className={CN.socialImg} />
                </div>
              </div>
            </div>
          </div>
          <div className={CN.wrap}>
            {/*  NOTE: when variant="inFirstColumn" : push video in first column */}
            <VideoCard src={VIDEO_LINK} title={VIDEO_TITLE} showBadge badgeName="See how Slick Websites helped Jen" badgeVariant="outline" tag="Portfolio__video">
              <div className={CN.badgeContainer}>
                <Badge name="Why Slick Websites" variant="fine" />
              </div>
              <div className={CN.videoTitle}>More clicks = More Revenue!</div>
              <div className={CN.videoDescription}>The Slick way increases your website’s performance and most importantly results in a 200% increase in bookings!</div>
            </VideoCard>

            <div className={CN.pageShowcaseGap}>
              <div className={CN.title}>
                <div className={CN.title}>
                  Get a full website from only <CurrencySign />
                  49/month
                </div>
              </div>
              <div className={CN.subTitle}>Includes 4 pages as shown below</div>
            </div>

            <section ref={areaPricing}>
              <PagesShowcase tag="Portfolio__PagesShowcase" />
            </section>

            <section data-bem="Portfolio__Pricing">
              <div className={CN.pricingGap}>
                <Pricing tag="Portfolio__pricing" />
              </div>
            </section>

            <div className={CN.alertGap}>
              <AlertPanel tag="Portfolio__AlertPanel" variant="slick">
                <div className={CN.flex}>
                  <div className={CN.alertContainer}>
                    <Icon tag="PaymentTabs__users" variant="star" width="xl" />
                    <div className={CN.alertText}>
                      <span className={CN.extraBold}>Upgrade to Pro</span> - For just <CurrencySign />
                      99/month
                      <div>
                        Includes 4 Core Pages
                        <ul className={CN.proList}>
                          <li>{S.plus} Choice of 2 additional pages </li>
                          <li>{S.plus} Google Reviews & Insta Feed Integration</li>
                          <li>{S.plus} VIP Support</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              </AlertPanel>
            </div>
            <Banner tag="Portfolio__Banner">
              <div className={CN.bannerReaderContainer}>
                <div className={CN.readerBannerTitle}>Ready for a digital upgrade?</div>
                <div className={CN.readerBannerSubTitle}>Complete the design form below and we will send you a draft of your new website to review in 72 hours!</div>
                <div className={CN.bannerLink}>
                  <ExternalLink link={LINK_FORM}>
                    <Button onClick={alwaysNoop} variant="white" width="medium">
                      <span className={CN.buttonSpace}>COMPLETE FORM</span>
                    </Button>
                  </ExternalLink>
                </div>
              </div>
            </Banner>

            <div className={CN.showcaseGap}>
              <div className={CN.title}> Customer reviews</div>
            </div>
            <Showcase tag="Portfolio__showcase" />

            <WebsiteTips tag="Portfolio__WebsiteTips" />

            <Banner tag="Portfolio__Banner-form">
              <div className={CN.bannerReaderContainer}>
                <div className={CN.readerBannerTitle}>Ready for a digital upgrade?</div>
                <div className={CN.readerBannerSubTitle}>Complete the design form below and we will send you a draft of your new website to review in 24 hours!</div>
                <div className={CN.bannerLink}>
                  <ExternalLink link={LINK_FORM}>
                    <Button onClick={alwaysNoop} variant="white" width="medium">
                      <span className={CN.buttonSpace}>COMPLETE FORM</span>
                    </Button>
                  </ExternalLink>
                </div>
              </div>
            </Banner>
            <div className={CN.showcaseGap}></div>
          </div>
        </div>
      )}
    </div>
  );
};

const mapStateToProps = (state) => ({
  isWebsiteCustomer: selectCurrentSalonWebsiteCustomer(state),
});

const PortfolioHoc = connect(mapStateToProps)(Portfolio);

export default PortfolioHoc;
